/**
 * @fileoverview configuration information and groupings for step types to avoid repeated step type checking. This is
 * joined with knockout viewmodels for each step in workflow-step-configuration
 */

import type { StepType } from "@lib/canvasStepConstants";
import sharedConstants from "@lib/shared_constants";
import { assertType } from "@lib/typeHelpers";
import type { Equals, ReverseMap } from "@lib/typeHelpers";

const SHARED_CONSTANTS_STEP_CLASS_KEYS = sharedConstants.WORKFLOW.STEP_CLASS_KEYS;

type StepClassKey = Exclude<
  (typeof SHARED_CONSTANTS_STEP_CLASS_KEYS)[keyof typeof SHARED_CONSTANTS_STEP_CLASS_KEYS],
  null | "CONTROL"
>;

/**
 * mapping from StepClassKey to reactKey
 */
export const STEP_TYPE_SKELETON = {
  FULL: "full",
  AUDIENCE_PATHS: "audience-paths",
  DECISION_SPLIT: "decision-split",
  DELAY: "delay",
  MESSAGE: "message",
  FACEBOOK_AUDIENCE_SYNC: "facebook-audience",
  GOOGLE_AUDIENCE_SYNC: "google-audience",
  TIKTOK_AUDIENCE_SYNC: "tiktok-audience",
  AUDIENCE_SYNC: "audience-sync",
  // <DO NOT REMOVE THIS COMMENT FOR CanvasStepGenerator> (2)
  SEND_TO_DESTINATION: "send-to-destination",
  CONTENT_OPTIMIZATION: "content-optimization",
  USER_CENTRIC_OPTIMIZATION: "user-centric-optimization",
  CONTEXT_UPDATE_STEP: "context-update",
  LLM: "llm",
  ACTION_PATHS: "action-paths",
  EXPERIMENT: "experiment",
  USER_UPDATE: "user-update",
  FEATURE_FLAG: "feature-flag",
} as const;

//#region doing some assertion to make sure the types aren't skewed

export type TypeSkeleton = typeof STEP_TYPE_SKELETON;
export type SkeletonKey = keyof TypeSkeleton;
export type ReverseTypeSkeleton = ReverseMap<TypeSkeleton>;
export type ReverseSkeletonKey = keyof ReverseTypeSkeleton;

assertType<Equals<StepClassKey, SkeletonKey>>();

//#endregion

/**
 * defines a relationship between the react key and the class key
 */
export const stepClassKey = Object.fromEntries(
  Object.entries(STEP_TYPE_SKELETON).map(([key, value]) => [value, key])
) as ReverseMap<TypeSkeleton>;

// Steps here need to have all of their egress states handled
export const MULTIPLE_EGRESS_STEPS: SkeletonKey[] = [
  "ACTION_PATHS",
  "AUDIENCE_PATHS",
  "EXPERIMENT",
  "DECISION_SPLIT",
];

// Steps that have chandeliers all take up extra space and have to be treated accordingly
export const CHANDELIER_STEPS: SkeletonKey[] = ["ACTION_PATHS", "AUDIENCE_PATHS", "EXPERIMENT"];

const v1LegalSteps: SkeletonKey[] = [
  "FULL",
  "DELAY",
  "DECISION_SPLIT",
  "MESSAGE",
  "FACEBOOK_AUDIENCE_SYNC",
  "GOOGLE_AUDIENCE_SYNC",
  "AUDIENCE_PATHS",
  "ACTION_PATHS",
];

/**
 * keys of steps that can only appear in v2 Canvases
 */
export const V2_ONLY_STEPS: SkeletonKey[] = (Object.keys(STEP_TYPE_SKELETON) as SkeletonKey[]).filter(
  (type) => !v1LegalSteps.includes(type)
);

export const v2OnlySteps: StepType[] = V2_ONLY_STEPS.map((type) => STEP_TYPE_SKELETON[type]);

export const STEPS_THAT_INCLUDE_AUDIENCE_VALIDATION: SkeletonKey[] = ["FULL", "MESSAGE"];

export const stepsThatSendMessages: SkeletonKey[] = ["MESSAGE", "CONTENT_OPTIMIZATION"];
